// Voice for AI Office, using the browser's own speech features (free; Chrome and Edge).
//
//   Talking: tap the mic → LISTENING with the words appearing live → stops by itself after a pause (or tap the
//   mic again) → the final words are sent as the boss's message ("Said by voice").
//   Note: Chrome/Edge send the audio to Google/Microsoft to turn it into text; nothing is stored by AI Office.
//
//   Reading aloud: speechSynthesis with the employee's voice style (pitch/rate), a Bangla voice for Bangla text.
import { useCallback, useEffect, useRef, useState } from 'react';

const Recognition = typeof window !== 'undefined' && (window.SpeechRecognition || window.webkitSpeechRecognition);
export const canListen = !!Recognition;
export const canSpeak = typeof window !== 'undefined' && 'speechSynthesis' in window;

// The boss's language for listening. Bangla recognition also understands the usual English words in between.
export const LISTEN_LANGS = { 'bn-BD': 'বাংলা', 'en-US': 'English' };

const ERRORS = {
  'not-allowed': 'The browser is not allowed to use the microphone. Click the lock icon next to the address and allow the microphone.',
  'service-not-allowed': 'The browser is not allowed to use the microphone. Click the lock icon next to the address and allow the microphone.',
  'audio-capture': 'No microphone found. Check that one is plugged in.',
  network: 'Voice needs the internet (the browser turns speech into text online). Check the connection.',
  'language-not-supported': 'This browser cannot listen in that language. Try English, or use Chrome.',
};

export function useListen({ lang, onFinal, onError }) {
  const [listening, setListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const rec = useRef(null);
  const text = useRef('');
  const cancelled = useRef(false);
  const cb = useRef({ onFinal, onError });
  cb.current = { onFinal, onError };

  const stop = useCallback(() => { if (rec.current) rec.current.stop(); }, []);
  const cancel = useCallback(() => { cancelled.current = true; if (rec.current) rec.current.abort(); }, []);

  const start = useCallback(() => {
    if (!Recognition) { cb.current.onError('Talking by voice works in Chrome or Edge. In this browser, please type.'); return; }
    if (rec.current) { rec.current.stop(); return; } // tapping the mic again = done talking
    const r = new Recognition();
    r.lang = lang || 'bn-BD';
    r.interimResults = true;
    r.continuous = false; // ends by itself after a short pause
    r.maxAlternatives = 1;
    text.current = '';
    cancelled.current = false;
    r.onresult = (e) => {
      let finalText = '', interim = '';
      for (let i = 0; i < e.results.length; i++) {
        if (e.results[i].isFinal) finalText += e.results[i][0].transcript;
        else interim += e.results[i][0].transcript;
      }
      text.current = (finalText || interim).trim();
      setTranscript((finalText + (interim ? ` ${interim}` : '')).trim());
    };
    r.onerror = (e) => {
      if (e.error === 'no-speech' || e.error === 'aborted') return;
      cb.current.onError(ERRORS[e.error] || `Voice stopped (${e.error}). Try again, or type.`);
    };
    r.onend = () => {
      rec.current = null;
      setListening(false);
      setTranscript('');
      const said = text.current.trim();
      if (said && !cancelled.current) cb.current.onFinal(said);
    };
    rec.current = r;
    setTranscript('');
    setListening(true);
    try { r.start(); } catch (err) { rec.current = null; setListening(false); cb.current.onError('Could not start the microphone. Try again.'); }
  }, [lang]);

  useEffect(() => () => { if (rec.current) rec.current.abort(); }, []);
  return { listening, transcript, start, stop, cancel, supported: canListen };
}

// ---- reading aloud ---------------------------------------------------------------------------------------
const STYLE = {
  female_warm: { pitch: 1.12, rate: 1.0 },
  female_clear: { pitch: 1.22, rate: 1.05 },
  male_calm: { pitch: 0.82, rate: 0.95 },
};
// Bangla script decides the voice; the ৳ sign alone doesn't (it also appears in English-letter replies).
const isBangla = (t) => /[ঀ-৲৴-৿]/.test(t || '');

function pickVoice(lang) {
  const voices = canSpeak ? window.speechSynthesis.getVoices() : [];
  const base = lang.slice(0, 2);
  return voices.find((v) => v.lang === lang) || voices.find((v) => (v.lang || '').toLowerCase().startsWith(base)) || null;
}

// True when the computer has a voice that can read Bangla.
export const hasBanglaVoice = () => !!pickVoice('bn-BD');

// Speaks one reply. Returns false if it could not (e.g. no Bangla voice on this computer).
export function speakReply(text, voiceStyle, { onStart, onEnd } = {}) {
  if (!canSpeak || !text) return false;
  // Tables are read row by row ("Factory 1, taka 74,760."); the |---| line is skipped.
  const flat = String(text).split('\n').map((l) => {
    const t = l.trim();
    if (!t.startsWith('|')) return l;
    if (/^[\s:|-]+$/.test(t)) return '';
    return `${t.replace(/^\||\|$/g, '').split('|').map((c) => c.trim()).filter(Boolean).join(', ')}.`;
  }).join('\n');
  const clean = flat.replace(/[•*_#>`]/g, ' ').replace(/\s+/g, ' ').trim();
  const lang = isBangla(clean) ? 'bn-BD' : 'en-US';
  const spoken = clean.replace(/৳\s?/g, lang === 'bn-BD' ? 'টাকা ' : 'taka ');
  const voice = pickVoice(lang);
  if (lang === 'bn-BD' && !voice) return false;
  try {
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(spoken);
    u.lang = lang;
    if (voice) u.voice = voice;
    const s = STYLE[voiceStyle] || STYLE.female_warm;
    u.pitch = s.pitch; u.rate = s.rate;
    u.onstart = () => onStart && onStart();
    u.onend = () => onEnd && onEnd();
    u.onerror = () => onEnd && onEnd();
    window.speechSynthesis.speak(u);
    return true;
  } catch (_) {
    return false;
  }
}

export const stopSpeaking = () => { try { if (canSpeak) window.speechSynthesis.cancel(); } catch (_) { /* ignore */ } };

// Voices load late in Chrome: ask once so they are ready when the first reply comes.
if (canSpeak) { try { window.speechSynthesis.getVoices(); window.speechSynthesis.onvoiceschanged = () => window.speechSynthesis.getVoices(); } catch (_) { /* ignore */ } }
